import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FaChevronDown,
  FaChevronUp,
  FaFileDownload,
  FaGripVertical,
  FaPrint,
  FaShareAlt,
  FaTable,
} from "react-icons/fa";

import {
  FieldAppFuelPhotoButton,
  FieldAppPhotoPreviewModal,
  useFieldAppPhotoPreview,
} from "./FieldAppFuelPhoto";
import {
  filterFuelingRecords,
  formatFuelIntervalRate,
  formatHaPerHour,
  formatTankFullLabel,
  listFuelMachines,
  listFuelOperators,
  sumFuelingLiters,
  enrichFuelingRecords,
} from "./fuelSupplyMetrics";
import { fetchFuelingRecords } from "./fieldAppApi";
import {
  buildLastYearPeriod,
  DEFAULT_FUEL_TOTAL_CHIP_ORDER,
  FUEL_TOTAL_PERIOD_PRESETS,
  fuelTotalPeriodLabel,
  normalizeFuelTotalChipOrder,
  readStoredFuelTotalPeriod,
  resolveFuelTotalPeriod,
  todayIso,
  writeStoredFuelTotalPeriod,
} from "./fieldAppPeriodUtils";
import {
  buildTelemetryScheduleFileName,
  exportTelemetrySchedulePdf,
  printTelemetrySchedule,
} from "./fieldAppTelemetryScheduleExport";

import "./FieldAppFuelSupplySpreadsheet.css";
import "./FieldAppTelemetrySchedulePanel.css";
import "./FieldAppFuelControlMatrix.css";
import "../Operation/OperationFuelSupplyPanel.css";

const TOTAL_PRESETS_INLINE = [
  FUEL_TOTAL_PERIOD_PRESETS.LAST_DAY,
  FUEL_TOTAL_PERIOD_PRESETS.LAST_WEEK,
  FUEL_TOTAL_PERIOD_PRESETS.LAST_MONTH,
  FUEL_TOTAL_PERIOD_PRESETS.LAST_YEAR,
];

function formatFuelLiters(value) {
  return `${Number(value || 0).toLocaleString("pt-BR", {
    maximumFractionDigits: 1,
  })} L`;
}

function reorderChipOrder(order, fromId, toId) {
  if (!fromId || !toId || fromId === toId) {
    return order;
  }
  const next = [...order];
  const fromIndex = next.indexOf(fromId);
  const toIndex = next.indexOf(toId);
  if (fromIndex < 0 || toIndex < 0) {
    return order;
  }
  next.splice(fromIndex, 1);
  next.splice(toIndex, 0, fromId);
  return next;
}

function FuelTotalChip({
  preset,
  t,
  totalsLoading,
  inlineTotals,
  customTotal,
  totalCustomFrom,
  totalCustomTo,
  onCustomFromChange,
  onCustomToChange,
  isActive,
  onSelect,
  draggingPreset,
  dragOverPreset,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
}) {
  const isCustom = preset === FUEL_TOTAL_PERIOD_PRESETS.CUSTOM;
  const item = isCustom
    ? customTotal
    : inlineTotals[preset] || { liters: 0, count: 0 };

  const handleChipClick = (event) => {
    if (event.target.closest(".field-app-fuel-supply__total-chip-drag")) {
      return;
    }
    if (event.target.closest("input")) {
      return;
    }
    onSelect(preset);
  };

  return (
    <div
      className={[
        "field-app-fuel-supply__total-chip",
        isCustom ? "field-app-fuel-supply__total-chip--custom" : "",
        isActive ? "field-app-fuel-supply__total-chip--active" : "",
        "field-app-fuel-supply__total-chip--selectable",
        draggingPreset === preset ? "field-app-fuel-supply__total-chip--dragging" : "",
        dragOverPreset === preset ? "field-app-fuel-supply__total-chip--drag-over" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={handleChipClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(preset);
        }
      }}
      role="button"
      tabIndex={0}
      aria-pressed={isActive}
      onDragOver={(event) => onDragOver(event, preset)}
      onDragLeave={onDragLeave}
      onDrop={(event) => onDrop(event, preset)}
      title={t("{{count}} abastecimento(s) — clique para ver na planilha", {
        count: item.count,
      })}
    >
      <button
        type="button"
        className="field-app-fuel-supply__total-chip-drag"
        draggable
        onClick={(event) => event.stopPropagation()}
        onDragStart={(event) => onDragStart(event, preset)}
        onDragEnd={onDragEnd}
        aria-label={t("Arrastar para reordenar {{label}}", {
          label: fuelTotalPeriodLabel(preset, t),
        })}
        title={t("Arrastar para reordenar")}
      >
        <FaGripVertical aria-hidden="true" />
      </button>

      <span className="field-app-fuel-supply__total-chip-label">
        {fuelTotalPeriodLabel(preset, t)}
      </span>

      {isCustom ? (
        <div className="field-app-fuel-supply__total-custom-fields">
          <label className="field-app-fuel-supply__total-field">
            <span>{t("De")}</span>
            <input
              type="date"
              value={totalCustomFrom}
              max={totalCustomTo || undefined}
              onChange={onCustomFromChange}
              aria-label={t("Data inicial do consumo personalizado")}
            />
          </label>
          <label className="field-app-fuel-supply__total-field">
            <span>{t("Até")}</span>
            <input
              type="date"
              value={totalCustomTo}
              min={totalCustomFrom || undefined}
              max={todayIso()}
              onChange={onCustomToChange}
              aria-label={t("Data final do consumo personalizado")}
            />
          </label>
        </div>
      ) : null}

      <strong className="field-app-fuel-supply__total-chip-value">
        {!isCustom && totalsLoading ? "…" : formatFuelLiters(item.liters)}
      </strong>
    </div>
  );
}

function computePresetTotal(records, preset, filters) {
  const period = resolveFuelTotalPeriod(preset);
  const filtered = filterFuelingRecords(records, {
    dateFrom: period.dateFrom.slice(0, 10),
    dateTo: period.dateTo.slice(0, 10),
    operatorId: filters.operatorId,
    machineIdent: filters.machineIdent,
  });
  return {
    liters: sumFuelingLiters(filtered),
    count: filtered.length,
  };
}

function FuelSupplyRow({ row, formatDateTime, t, onOpenDetail, onOpenPhoto, isExample }) {
  return (
    <tr
      key={row.uuid}
      className={isExample ? "field-app-fuel-example-row" : undefined}
    >
      <td>{formatDateTime(row.timestamp)}</td>
      <td>{row.equipmentName || row.equipmentId || "—"}</td>
      <td>{row.operatorName || "—"}</td>
      <td>{row.liters != null ? Number(row.liters).toLocaleString("pt-BR") : "—"}</td>
      <td>
        {row.hour_meter != null && row.hour_meter !== ""
          ? Number(row.hour_meter).toLocaleString("pt-BR")
          : "—"}
      </td>
      <td>
        {row.panel_hectares != null && row.panel_hectares !== ""
          ? Number(row.panel_hectares).toLocaleString("pt-BR", {
              maximumFractionDigits: 1,
            })
          : "—"}
      </td>
      <td>
        <span
          className={`operation-fuel-tank-badge operation-fuel-tank-badge--${
            row.tankFull === true
              ? "full"
              : row.tankFull === false
                ? "partial"
                : "unknown"
          }`}
        >
          {formatTankFullLabel(row, t)}
        </span>
        {row.partialFillCount > 0 && row.hasFuelInterval ? (
          <div className="field-app-fuel-partial-note">
            {t("Inclui parciais no intervalo")}
          </div>
        ) : null}
      </td>
      <td className="fuel-metric-cell">
        {row.hasFuelInterval
          ? formatFuelIntervalRate(row.intervalLitersPerHour, t("L/h"))
          : "—"}
      </td>
      <td className="fuel-metric-cell">
        {row.hasFuelInterval
          ? formatFuelIntervalRate(row.intervalLitersPerHa, t("L/ha"))
          : "—"}
      </td>
      <td className="fuel-metric-cell">
        {row.hasFuelInterval ? formatHaPerHour(row.intervalHaPerHour) : "—"}
      </td>
      <td>
        <FieldAppFuelPhotoButton
          photoUrl={row.panelPhotoUrl}
          hasPhoto={row.hasPanelPhoto || row.panelPhotoUrl}
          onOpenPhoto={onOpenPhoto}
          t={t}
        />
      </td>
      <td>{row.observations || "—"}</td>
      <td>
        {isExample ? (
          <span className="text-muted">{t("Exemplo")}</span>
        ) : (
          <button
            type="button"
            className="btn btn-sm btn-default"
            onClick={() => onOpenDetail(row)}
          >
            {t("Detalhe")}
          </button>
        )}
      </td>
    </tr>
  );
}

function FuelSupplyTable({ rows, formatDateTime, t, onOpenDetail, onOpenPhoto, isExample }) {
  return (
    <table className="table table-striped jambo_table field-app-fuel-supply__table">
      <thead>
        <tr>
          <th>{t("Data")}</th>
          <th>{t("Máquina")}</th>
          <th>{t("Operador")}</th>
          <th>{t("Litros")}</th>
          <th>{t("Horímetro")}</th>
          <th>{t("Hectares painel")}</th>
          <th>{t("Tanque")}</th>
          <th>{t("L/h intervalo")}</th>
          <th>{t("L/ha intervalo")}</th>
          <th>{t("ha/h intervalo")}</th>
          <th>{t("Foto")}</th>
          <th>{t("Observações")}</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <FuelSupplyRow
            key={row.uuid}
            row={row}
            formatDateTime={formatDateTime}
            t={t}
            onOpenDetail={onOpenDetail}
            onOpenPhoto={onOpenPhoto}
            isExample={isExample}
          />
        ))}
      </tbody>
    </table>
  );
}

export default function FieldAppFuelSupplySpreadsheet({
  rows = [],
  showingExample = false,
  exampleRows = [],
  formatDateTime,
  t,
  onOpenDetail,
  periodLabel,
  operatorLabel,
  machineLabel,
  initialOperatorId = "",
  initialMachineIdent = "",
  defaultExpanded = true,
  farmId,
  baseSelection = null,
}) {
  const storedTotalPeriod = useMemo(
    () => (farmId ? readStoredFuelTotalPeriod(farmId) : null),
    [farmId]
  );

  const [expanded, setExpanded] = useState(defaultExpanded);
  const [filterOperatorId, setFilterOperatorId] = useState(initialOperatorId);
  const [filterMachineIdent, setFilterMachineIdent] = useState(initialMachineIdent);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [totalCustomFrom, setTotalCustomFrom] = useState(
    (storedTotalPeriod && storedTotalPeriod.customFrom) || todayIso()
  );
  const [totalCustomTo, setTotalCustomTo] = useState(
    (storedTotalPeriod && storedTotalPeriod.customTo) || todayIso()
  );
  const [totalYearRows, setTotalYearRows] = useState([]);
  const [totalCustomRows, setTotalCustomRows] = useState([]);
  const [totalsLoading, setTotalsLoading] = useState(false);
  const [chipOrder, setChipOrder] = useState(() =>
    normalizeFuelTotalChipOrder(
      (storedTotalPeriod && storedTotalPeriod.chipOrder) ||
        DEFAULT_FUEL_TOTAL_CHIP_ORDER
    )
  );
  const [draggingPreset, setDraggingPreset] = useState(null);
  const [dragOverPreset, setDragOverPreset] = useState(null);
  const [activeTablePreset, setActiveTablePreset] = useState(
    () =>
      (storedTotalPeriod && storedTotalPeriod.activeTablePreset) ||
      FUEL_TOTAL_PERIOD_PRESETS.LAST_MONTH
  );
  const [sharing, setSharing] = useState(false);
  const { previewUrl, openPhoto, closePhoto } = useFieldAppPhotoPreview();
  const printAreaRef = useRef(null);
  const tableSectionRef = useRef(null);

  const operatorOptions = useMemo(() => listFuelOperators(rows), [rows]);
  const machineOptions = useMemo(() => listFuelMachines(rows), [rows]);

  const filteredRows = useMemo(() => {
    if (showingExample) return exampleRows;
    return filterFuelingRecords(rows, {
      operatorId: filterOperatorId,
      machineIdent: filterMachineIdent,
    });
  }, [
    showingExample,
    exampleRows,
    rows,
    filterOperatorId,
    filterMachineIdent,
  ]);

  const totalFilters = useMemo(
    () => ({
      operatorId: filterOperatorId,
      machineIdent: filterMachineIdent,
    }),
    [filterOperatorId, filterMachineIdent]
  );

  const totalPeriodCustom = useMemo(
    () => resolveFuelTotalPeriod(FUEL_TOTAL_PERIOD_PRESETS.CUSTOM, totalCustomFrom, totalCustomTo),
    [totalCustomFrom, totalCustomTo]
  );

  useEffect(() => {
    if (farmId) {
      writeStoredFuelTotalPeriod(farmId, {
        customFrom: totalCustomFrom,
        customTo: totalCustomTo,
        activeTablePreset,
      });
    }
  }, [farmId, totalCustomFrom, totalCustomTo, activeTablePreset]);

  const activeTablePeriod = useMemo(() => {
    if (!activeTablePreset) return null;
    if (activeTablePreset === FUEL_TOTAL_PERIOD_PRESETS.CUSTOM) {
      return totalPeriodCustom;
    }
    return resolveFuelTotalPeriod(activeTablePreset);
  }, [activeTablePreset, totalPeriodCustom]);

  const activeTablePeriodLabel = useMemo(() => {
    if (!activeTablePreset) return periodLabel || "";
    if (activeTablePreset === FUEL_TOTAL_PERIOD_PRESETS.CUSTOM) {
      const fromLabel = totalCustomFrom;
      const toLabel = totalCustomTo;
      if (fromLabel === toLabel) return fromLabel;
      return `${fromLabel} — ${toLabel}`;
    }
    return fuelTotalPeriodLabel(activeTablePreset, t);
  }, [activeTablePreset, periodLabel, t, totalCustomFrom, totalCustomTo]);

  useEffect(() => {
    if (!farmId || !baseSelection || showingExample) {
      setTotalYearRows([]);
      return undefined;
    }

    let cancelled = false;
    const yearPeriod = buildLastYearPeriod();

    async function loadYearTotals() {
      setTotalsLoading(true);
      try {
        const selection = {
          ...baseSelection,
          dateFrom: yearPeriod.dateFrom,
          dateTo: yearPeriod.dateTo,
        };
        const data = await fetchFuelingRecords(
          farmId,
          { limit: 5000 },
          selection
        );
        if (!cancelled) {
          setTotalYearRows(data.results || []);
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setTotalYearRows([]);
        }
      } finally {
        if (!cancelled) {
          setTotalsLoading(false);
        }
      }
    }

    loadYearTotals();
    return () => {
      cancelled = true;
    };
  }, [farmId, baseSelection, showingExample]);

  useEffect(() => {
    if (!farmId || !baseSelection || showingExample) {
      setTotalCustomRows([]);
      return undefined;
    }

    let cancelled = false;

    async function loadCustomTotals() {
      try {
        const selection = {
          ...baseSelection,
          dateFrom: totalPeriodCustom.dateFrom,
          dateTo: totalPeriodCustom.dateTo,
        };
        const data = await fetchFuelingRecords(
          farmId,
          { limit: 5000 },
          selection
        );
        if (!cancelled) {
          setTotalCustomRows(data.results || []);
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setTotalCustomRows([]);
        }
      }
    }

    loadCustomTotals();
    return () => {
      cancelled = true;
    };
  }, [farmId, baseSelection, totalPeriodCustom, showingExample]);

  const inlineTotals = useMemo(() => {
    if (showingExample) return {};
    return TOTAL_PRESETS_INLINE.reduce((acc, preset) => {
      acc[preset] = computePresetTotal(totalYearRows, preset, totalFilters);
      return acc;
    }, {});
  }, [showingExample, totalYearRows, totalFilters]);

  const customTotal = useMemo(() => {
    if (showingExample) {
      return { liters: 0, count: 0 };
    }
    const filtered = filterFuelingRecords(totalCustomRows, {
      dateFrom: totalPeriodCustom.dateFrom.slice(0, 10),
      dateTo: totalPeriodCustom.dateTo.slice(0, 10),
      operatorId: filterOperatorId,
      machineIdent: filterMachineIdent,
    });
    return {
      liters: sumFuelingLiters(filtered),
      count: filtered.length,
    };
  }, [
    showingExample,
    totalCustomRows,
    totalPeriodCustom,
    filterOperatorId,
    filterMachineIdent,
  ]);

  const displayRows = useMemo(() => {
    if (showingExample) return exampleRows;

    if (!activeTablePreset) {
      return filteredRows;
    }

    if (!activeTablePeriod) {
      return [];
    }

    const sourceRecords =
      activeTablePreset === FUEL_TOTAL_PERIOD_PRESETS.CUSTOM
        ? totalCustomRows
        : totalYearRows;

    const periodFiltered = filterFuelingRecords(sourceRecords, {
      dateFrom: activeTablePeriod.dateFrom.slice(0, 10),
      dateTo: activeTablePeriod.dateTo.slice(0, 10),
      operatorId: filterOperatorId,
      machineIdent: filterMachineIdent,
    });

    return enrichFuelingRecords(periodFiltered).rows;
  }, [
    showingExample,
    exampleRows,
    activeTablePreset,
    filteredRows,
    totalCustomRows,
    totalYearRows,
    activeTablePeriod,
    filterOperatorId,
    filterMachineIdent,
  ]);

  const handlePrint = useCallback(() => {
    printTelemetrySchedule();
  }, []);

  const handleSelectTablePreset = useCallback((preset) => {
    setActiveTablePreset(preset);
    if (tableSectionRef.current) {
      tableSectionRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, []);

  const handleExportPdf = useCallback(async () => {
    if (!printAreaRef.current || exportingPdf) return;
    setExportingPdf(true);
    try {
      let exportOperatorLabel = operatorLabel;
      if (filterOperatorId) {
        const match = operatorOptions.find(
          (item) => String(item.id) === filterOperatorId
        );
        if (match && match.name) {
          exportOperatorLabel = match.name;
        }
      }
      await exportTelemetrySchedulePdf(
        printAreaRef.current,
        buildTelemetryScheduleFileName({
          operatorLabel: exportOperatorLabel,
          periodLabel: `abastecimentos-${activeTablePeriodLabel || periodLabel || ""}`,
        })
      );
    } finally {
      setExportingPdf(false);
    }
  }, [
    exportingPdf,
    filterOperatorId,
    operatorLabel,
    operatorOptions,
    periodLabel,
    activeTablePeriodLabel,
  ]);

  const handleShare = useCallback(async () => {
    if (sharing || !displayRows.length) return;
    setSharing(true);
    try {
      const periodText = activeTablePeriodLabel || periodLabel || t("Período");
      const totalText = formatFuelLiters(sumFuelingLiters(displayRows));
      const shareText = t(
        "Abastecimentos — {{period}}: {{total}} em {{count}} registro(s).",
        {
          period: periodText,
          total: totalText,
          count: displayRows.length,
        }
      );
      const sharePayload = {
        title: t("Planilha de abastecimentos"),
        text: shareText,
        url: window.location.href,
      };
      if (navigator.share) {
        await navigator.share(sharePayload);
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(
          `${sharePayload.title}\n${shareText}\n${sharePayload.url}`
        );
        window.alert(t("Resumo copiado para a área de transferência."));
      } else {
        window.prompt(t("Copie o resumo abaixo:"), `${shareText}\n${sharePayload.url}`);
      }
    } catch (error) {
      if (error && error.name !== "AbortError") {
        console.error(error);
      }
    } finally {
      setSharing(false);
    }
  }, [
    sharing,
    displayRows,
    activeTablePeriodLabel,
    periodLabel,
    t,
  ]);

  const handleChipDragStart = useCallback((event, preset) => {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", preset);
    setDraggingPreset(preset);
  }, []);

  const handleChipDragEnd = useCallback(() => {
    setDraggingPreset(null);
    setDragOverPreset(null);
  }, []);

  const handleChipDragOver = useCallback((event, preset) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setDragOverPreset(preset);
  }, []);

  const handleChipDragLeave = useCallback(() => {
    setDragOverPreset(null);
  }, []);

  const handleChipDrop = useCallback(
    (event, preset) => {
      event.preventDefault();
      const fromPreset = event.dataTransfer.getData("text/plain");
      setChipOrder((current) => {
        const next = reorderChipOrder(current, fromPreset, preset);
        if (farmId) {
          writeStoredFuelTotalPeriod(farmId, { chipOrder: next });
        }
        return next;
      });
      setDraggingPreset(null);
      setDragOverPreset(null);
    },
    [farmId]
  );

  const renderTotalToolbar = () => {
    if (showingExample) return null;

    return (
      <div className="field-app-fuel-supply__toolbar-totals">
        {chipOrder.map((preset) => (
          <FuelTotalChip
            key={preset}
            preset={preset}
            t={t}
            totalsLoading={totalsLoading}
            inlineTotals={inlineTotals}
            customTotal={customTotal}
            totalCustomFrom={totalCustomFrom}
            totalCustomTo={totalCustomTo}
            onCustomFromChange={(event) => {
              setTotalCustomFrom(event.target.value);
              setActiveTablePreset(FUEL_TOTAL_PERIOD_PRESETS.CUSTOM);
            }}
            onCustomToChange={(event) => {
              setTotalCustomTo(event.target.value);
              setActiveTablePreset(FUEL_TOTAL_PERIOD_PRESETS.CUSTOM);
            }}
            isActive={activeTablePreset === preset}
            onSelect={handleSelectTablePreset}
            draggingPreset={draggingPreset}
            dragOverPreset={dragOverPreset}
            onDragStart={handleChipDragStart}
            onDragEnd={handleChipDragEnd}
            onDragOver={handleChipDragOver}
            onDragLeave={handleChipDragLeave}
            onDrop={handleChipDrop}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="field-app-fuel-supply">
      <div className="field-app-fuel-supply__header">
        <button
          type="button"
          className="field-app-fuel-supply__toggle btn btn-default"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
        >
          <FaTable aria-hidden="true" />
          <span>{t("Planilha de abastecimentos")}</span>
          {expanded ? (
            <FaChevronUp aria-hidden="true" />
          ) : (
            <FaChevronDown aria-hidden="true" />
          )}
        </button>

        {expanded && !showingExample ? (
          <div className="field-app-fuel-supply__filters">
            <label className="field-app-fuel-supply__filter">
              <span>{t("Operador")}</span>
              <select
                value={filterOperatorId}
                onChange={(event) => setFilterOperatorId(event.target.value)}
              >
                <option value="">{t("Todos os operadores")}</option>
                {operatorOptions.map((item) => (
                  <option key={item.id} value={String(item.id)}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field-app-fuel-supply__filter">
              <span>{t("Máquina")}</span>
              <select
                value={filterMachineIdent}
                onChange={(event) => setFilterMachineIdent(event.target.value)}
              >
                <option value="">{t("Todas as máquinas")}</option>
                {machineOptions.map((item) => (
                  <option key={item.ident} value={item.ident}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        ) : null}
      </div>

      {expanded ? (
        <div className="field-app-fuel-supply__body field-app-telemetry-schedule">
          {showingExample ? (
            <p className="field-app-telemetry-schedule__hint">
              {t(
                "Nenhum abastecimento no período selecionado — amplie o filtro de datas (ex.: Último mês) ou confira a aba Eventos. A planilha abaixo é só ilustrativa."
              )}
            </p>
          ) : (
            <p className="field-app-fuel-supply__count">
              {t("{{count}} abastecimento(s) — {{period}}", {
                count: displayRows.length,
                period: activeTablePeriodLabel || periodLabel || t("Período"),
              })}
              {filterOperatorId || filterMachineIdent
                ? ` · ${t("filtro ativo")}`
                : ""}
              {totalsLoading && activeTablePreset !== FUEL_TOTAL_PERIOD_PRESETS.CUSTOM
                ? ` · ${t("carregando…")}`
                : ""}
            </p>
          )}

          {!showingExample ? (
            <div className="field-app-telemetry-schedule__toolbar field-app-fuel-supply__toolbar">
              <div className="field-app-telemetry-schedule__toolbar-group">
                <button
                  type="button"
                  className="btn btn-default field-app-telemetry-schedule__toolbar-btn"
                  onClick={handlePrint}
                  disabled={!displayRows.length}
                  aria-label={t("Imprimir planilha em A4 paisagem")}
                  title={t("Imprimir planilha em A4 paisagem")}
                >
                  <FaPrint aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="btn btn-default field-app-telemetry-schedule__toolbar-btn"
                  onClick={handleExportPdf}
                  disabled={exportingPdf || showingExample || !displayRows.length}
                  aria-label={t("Salvar planilha em PDF (A4 paisagem)")}
                  title={
                    exportingPdf
                      ? t("Gerando PDF…")
                      : t("Salvar planilha em PDF (A4 paisagem)")
                  }
                >
                  <FaFileDownload aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="btn btn-default field-app-telemetry-schedule__toolbar-btn"
                  onClick={handleShare}
                  disabled={sharing || !displayRows.length}
                  aria-label={t("Compartilhar resumo da planilha")}
                  title={
                    sharing
                      ? t("Compartilhando…")
                      : t("Compartilhar resumo da planilha")
                  }
                >
                  <FaShareAlt aria-hidden="true" />
                </button>
              </div>
              {renderTotalToolbar()}
            </div>
          ) : null}

          {displayRows.length ? (
            <>
              <div
                ref={tableSectionRef}
                className="field-app-fuel-supply__table-section"
              >
                <div
                  ref={printAreaRef}
                  className="field-app-telemetry-schedule__print-area field-app-fueling-table-wrap table-responsive"
                >
                  <div className="field-app-telemetry-schedule__print-header">
                    <strong>{t("Planilha de abastecimentos")}</strong>
                    {activeTablePeriodLabel ? (
                      <span>{activeTablePeriodLabel}</span>
                    ) : null}
                    {operatorLabel ? <span>{operatorLabel}</span> : null}
                    {machineLabel ? <span>{machineLabel}</span> : null}
                  </div>
                  <FuelSupplyTable
                    rows={displayRows}
                    formatDateTime={formatDateTime}
                    t={t}
                    onOpenDetail={onOpenDetail}
                    onOpenPhoto={openPhoto}
                    isExample={showingExample}
                  />
                </div>
              </div>
            </>
          ) : !showingExample ? (
            <p
              ref={tableSectionRef}
              className="field-app-telemetry-schedule__hint field-app-fuel-supply__table-section"
            >
              {t("Nenhum abastecimento corresponde aos filtros selecionados.")}
            </p>
          ) : null}
        </div>
      ) : null}
      <FieldAppPhotoPreviewModal url={previewUrl} onClose={closePhoto} t={t} />
    </div>
  );
}
